import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { auth } from '../lib/api'

const UserContext = createContext(null)

// Session configuration defaults (will be updated from server)
const DEFAULT_SESSION_CONFIG = {
  inactivityTimeoutMinutes: 120, // 2 hours
  maxSessionDays: 7
}

// Read cache ONCE at module level to ensure synchronous access
const getInitialUser = () => {
  try {
    const cached = sessionStorage.getItem('iceops_user')
    return cached ? JSON.parse(cached) : null
  } catch {
    return null
  }
}

const getInitialAuthStatus = () => {
  try {
    // First check sessionStorage (same-origin cache)
    const cached = sessionStorage.getItem('iceops_auth')
    if (cached === 'true') return true

    // Then check for cross-subdomain auth indicator cookie
    // This cookie is set by the server and shared across subdomains
    if (document.cookie.includes('auth_active=1')) {
      return true
    }

    return false
  } catch {
    return false
  }
}

const getLastActivity = () => {
  try {
    const cached = localStorage.getItem('iceops_last_activity')
    return cached ? parseInt(cached, 10) : Date.now()
  } catch {
    return Date.now()
  }
}

export function UserProvider({ children }) {
  // Use initial values from cache - read once, no function calls during render
  const initialUser = getInitialUser()
  const initialAuth = getInitialAuthStatus()

  const [user, setUser] = useState(initialUser)
  const [isAuthenticated, setIsAuthenticated] = useState(initialAuth)
  const [sessionConfig, setSessionConfig] = useState(DEFAULT_SESSION_CONFIG)

  // Track if this is initial load with cached data to prevent flash
  const hasCachedDataRef = useRef(!!initialUser)
  const initialRefreshDoneRef = useRef(false)

  // Use refs to avoid recreating refreshUser callback
  const userRef = useRef(user)
  const isAuthenticatedRef = useRef(isAuthenticated)

  // Inactivity timeout refs
  const inactivityTimerRef = useRef(null)
  const lastActivityRef = useRef(getLastActivity())

  // Prevent concurrent refreshUser calls to avoid 429 errors
  const isRefreshingRef = useRef(false)
  const refreshPromiseRef = useRef(null)

  // Keep refs in sync with state
  useEffect(() => {
    userRef.current = user
    isAuthenticatedRef.current = isAuthenticated
  }, [user, isAuthenticated])

  // Refresh user data from server
  const refreshUser = useCallback(async () => {
    // If already refreshing, return the existing promise to prevent concurrent calls
    if (isRefreshingRef.current && refreshPromiseRef.current) {
      return refreshPromiseRef.current
    }

    isRefreshingRef.current = true

    // Create and store the promise
    const promise = (async () => {
      try {
        // Get user with HttpOnly cookie (no need for separate isAuthenticated call - both hit same endpoint)
        const result = await auth.getUser()

        // Check if result is an error indicator (network/rate limit errors)
        if (result && result._error) {
          // Keep using cached data for transient errors - don't clear auth
          initialRefreshDoneRef.current = true
          return
        }

        if (result && !result._error) {
          const currentUser = result
          // ALWAYS check if data actually changed before calling setState
          const currentUserStr = JSON.stringify(currentUser)
          const cachedUserStr = JSON.stringify(userRef.current)

          // Check if anything actually changed
          const userChanged = cachedUserStr !== currentUserStr
          const authChanged = isAuthenticatedRef.current !== true

          // Only update state if values actually changed
          if (userChanged) {
            setUser(currentUser)
          }
          if (authChanged) {
            setIsAuthenticated(true)
          }

          // Always update cache
          sessionStorage.setItem('iceops_user', currentUserStr)
          sessionStorage.setItem('iceops_auth', 'true')
          initialRefreshDoneRef.current = true
        } else {
          // result is null - actual auth failure (401, expired session, etc.)
          // Only clear auth state, don't change state if already in correct state
          const userNeedsUpdate = userRef.current !== null
          const authNeedsUpdate = isAuthenticatedRef.current !== false

          // Only update state if values need to change
          if (userNeedsUpdate) {
            setUser(null)
          }
          if (authNeedsUpdate) {
            setIsAuthenticated(false)
          }
          sessionStorage.removeItem('iceops_user')
          sessionStorage.removeItem('iceops_auth')
          initialRefreshDoneRef.current = true
        }
      } catch (error) {
        // Unexpected errors - keep cached data to avoid logging users out unnecessarily
        console.error('Unexpected error refreshing user:', error)
        initialRefreshDoneRef.current = true
      } finally {
        isRefreshingRef.current = false
        refreshPromiseRef.current = null
      }
    })()

    refreshPromiseRef.current = promise
    return promise
  }, [])

  // Logout function
  const logout = useCallback(async (reason = 'manual') => {
    // Clear inactivity timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current)
      inactivityTimerRef.current = null
    }

    try {
      await auth.logout()
    } catch (error) {
      console.error('Logout error:', error)
    }

    // Clear state
    setUser(null)
    setIsAuthenticated(false)
    sessionStorage.removeItem('iceops_user')
    sessionStorage.removeItem('iceops_auth')
    localStorage.removeItem('iceops_last_activity')

    // Redirect to login with reason
    if (reason === 'inactivity') {
      window.location.href = '/login?expired=inactivity'
    } else if (reason === 'session_expired') {
      window.location.href = '/login?expired=session'
    }
  }, [])

  // Update last activity timestamp
  const updateActivity = useCallback(() => {
    const now = Date.now()
    lastActivityRef.current = now
    localStorage.setItem('iceops_last_activity', now.toString())
  }, [])

  // Check for inactivity and schedule logout
  const checkInactivity = useCallback(() => {
    if (!isAuthenticatedRef.current) return

    const now = Date.now()
    const lastActivity = lastActivityRef.current
    const inactivityMs = sessionConfig.inactivityTimeoutMinutes * 60 * 1000
    const timeSinceActivity = now - lastActivity

    if (timeSinceActivity >= inactivityMs) {
      // User has been inactive too long - log them out
      logout('inactivity')
    } else {
      // Schedule next check
      const timeUntilExpiry = inactivityMs - timeSinceActivity
      const checkInterval = Math.min(timeUntilExpiry, 60000) // Check at most every minute

      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current)
      }
      inactivityTimerRef.current = setTimeout(checkInactivity, checkInterval)
    }
  }, [sessionConfig.inactivityTimeoutMinutes, logout])

  // Set up activity tracking
  useEffect(() => {
    if (!isAuthenticated) return

    // Activity events to track
    const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll']

    // Throttled activity handler (update at most once per 30 seconds)
    let lastUpdate = 0
    const handleActivity = () => {
      const now = Date.now()
      if (now - lastUpdate > 30000) {
        lastUpdate = now
        updateActivity()
      }
    }

    // Add event listeners
    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true })
    })

    // Initial activity update
    updateActivity()

    // Start inactivity checking
    checkInactivity()

    return () => {
      // Remove event listeners
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity)
      })

      // Clear timer
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current)
      }
    }
  }, [isAuthenticated, updateActivity, checkInactivity])

  // Fetch session config on mount
  useEffect(() => {
    const fetchSessionConfig = async () => {
      try {
        const response = await fetch('/api/auth/session-config')
        if (response.ok) {
          const config = await response.json()
          setSessionConfig(config)
        }
      } catch (error) {
        console.error('Failed to fetch session config:', error)
      }
    }
    fetchSessionConfig()
  }, [])

  // Initialize user on mount
  useEffect(() => {
    // If we have cached auth data (e.g., just came from magic link),
    // add a small delay to ensure cookies are established after redirect
    const delay = hasCachedDataRef.current ? 100 : 0

    const timeoutId = setTimeout(() => {
      refreshUser()
    }, delay)

    // Listen for profile updates
    const handleProfileUpdate = () => {
      refreshUser()
    }
    window.addEventListener('profileUpdated', handleProfileUpdate)

    // Listen for session expiry from API responses
    const handleSessionExpired = () => {
      logout('session_expired')
    }
    window.addEventListener('sessionExpired', handleSessionExpired)

    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener('profileUpdated', handleProfileUpdate)
      window.removeEventListener('sessionExpired', handleSessionExpired)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Helper function to check if user has a specific role
  const hasRole = useCallback((role) => {
    if (!user) return false
    // Check primary role
    if (user.role === role) return true
    // Check additional roles array
    if (user.roles && Array.isArray(user.roles)) {
      return user.roles.includes(role)
    }
    return false
  }, [user])

  const value = useMemo(() => ({
    user,
    isAuthenticated,
    refreshUser,
    setUser,
    setIsAuthenticated,
    hasRole,
    logout,
    sessionConfig
  }), [user, isAuthenticated, refreshUser, hasRole, logout, sessionConfig])

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}

export function useUser() {
  const context = useContext(UserContext)
  if (!context) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
}
